import { useState, useCallback, useEffect } from 'react';
import type { Draft, ProductFormData } from '@/types';
import { api } from '@/lib/api';

const DRAFT_STORAGE_KEY = 'sggadgets_uploader_drafts';
const AUTO_SAVE_INTERVAL = 30000;

function getLocalDrafts(): Draft[] {
  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveLocalDrafts(drafts: Draft[]): void {
  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts));
}

export function useDrafts() {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    setDrafts(getLocalDrafts());
  }, []);

  const createDraft = useCallback(
    (data: Partial<ProductFormData>): Draft => {
      const draft: Draft = {
        id: crypto.randomUUID(),
        data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const updated = [draft, ...drafts];
      setDrafts(updated);
      saveLocalDrafts(updated);
      setCurrentDraftId(draft.id);
      return draft;
    },
    [drafts],
  );

  const updateDraft = useCallback(
    (id: string, data: Partial<ProductFormData>): Draft | null => {
      const updated = drafts.map((d) =>
        d.id === id
          ? { ...d, data: { ...d.data, ...data }, updatedAt: new Date().toISOString() }
          : d,
      );
      setDrafts(updated);
      saveLocalDrafts(updated);
      setLastSaved(new Date());
      return updated.find((d) => d.id === id) || null;
    },
    [drafts],
  );

  const deleteDraft = useCallback(
    (id: string) => {
      const updated = drafts.filter((d) => d.id !== id);
      setDrafts(updated);
      saveLocalDrafts(updated);
      if (currentDraftId === id) {
        setCurrentDraftId(null);
      }
      api.drafts.delete(id).catch(() => {});
    },
    [drafts, currentDraftId],
  );

  const loadDraft = useCallback(
    (id: string): Draft | null => {
      const draft = drafts.find((d) => d.id === id);
      if (draft) {
        setCurrentDraftId(id);
      }
      return draft || null;
    },
    [drafts],
  );

  const syncToBackend = useCallback(async () => {
    for (const draft of drafts) {
      try {
        await api.drafts.save(draft.data);
      } catch {
        // Backend sync is best-effort
      }
    }
  }, [drafts]);

  useEffect(() => {
    if (drafts.length === 0) return;

    const interval = setInterval(() => {
      syncToBackend();
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [drafts, syncToBackend]);

  const autoSave = useCallback(
    (data: Partial<ProductFormData>) => {
      if (currentDraftId) {
        updateDraft(currentDraftId, data);
      } else {
        createDraft(data);
      }
    },
    [currentDraftId, createDraft, updateDraft],
  );

  return {
    drafts,
    currentDraftId,
    lastSaved,
    createDraft,
    updateDraft,
    deleteDraft,
    loadDraft,
    autoSave,
    syncToBackend,
  };
}
